import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
} from "antd";
import api from "../services/api";
import moment from "moment";

const { Option } = Select;
const { RangePicker } = DatePicker;

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [form] = Form.useForm();

  // ==========================
  //   Fetch Tournaments
  // ==========================
  const fetchTournaments = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await api.get("/tournaments", {
        params: { page, pageSize },
      });

      const { data, total } = res.data;
      setTournaments(data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
    } catch (error) {
      console.error(error);
      message.error("Failed to load tournaments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments(pagination.current, pagination.pageSize);
  }, []);

  // ==========================
  //   Delete Tournament
  // ==========================
  const handleDelete = async (id) => {
    try {
      await api.delete(`/tournaments/${id}`);
      message.success("Tournament deleted successfully");
      fetchTournaments(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error);
      message.error("Failed to delete tournament");
    }
  };

  // ==========================
  //   Edit Tournament
  // ==========================
  const handleEdit = (record) => {
    setIsEditing(true);
    setIsModalVisible(true);

    form.setFieldsValue({
      tournament_id: record.tournament_id,
      name: record.name,
      sport: record.sport,
      category: record.category,
      status: record.status,
      level: record.level,
      date_range: [
        moment(record.start_date),
        moment(record.end_date),
      ],
    });
  };

  // ==========================
  //   Create Tournament
  // ==========================
  const handleCreate = () => {
    setIsEditing(false);
    form.resetFields();
    setIsModalVisible(true);
  };

  // ==========================
  //   Save Tournament
  // ==========================
  const handleSubmit = async (values) => {
    try {
      const [start, end] = values.date_range;

      const payload = {
        name: values.name,
        sport: values.sport,
        category: values.category,
        status: values.status,
        level: values.level,
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
      };

      if (values.tournament_id) {
        await api.put(`/tournaments/${values.tournament_id}`, payload);
        message.success("Tournament updated successfully");
      } else {
        await api.post("/tournaments", payload);
        message.success("Tournament created successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchTournaments(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error);
      message.error("Failed to save tournament");
    }
  };

  // ==========================
  //   Table Columns
  // ==========================
  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Sport", dataIndex: "sport" },
    { title: "Category", dataIndex: "category" },
    { title: "Status", dataIndex: "status" },
    { title: "Level", dataIndex: "level" },
    { title: "Start Date", dataIndex: "start_date" },
    { title: "End Date", dataIndex: "end_date" },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>

          <Popconfirm
            title="Are you sure to delete this tournament?"
            onConfirm={() => handleDelete(record.tournament_id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleTableChange = (p) => {
    fetchTournaments(p.current, p.pageSize);
  };

  // ==========================
  //   JSX Rendering
  // ==========================
  return (
    <div style={{ padding: 10 }}>
      
      {/* Header */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 20, margin: 0 }}>Tournaments</h2>
        <Button type="primary" onClick={handleCreate}>
          Create Tournament
        </Button>
      </div>

      {/* Mobile scrollable table */}
      <div style={{ overflowX: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={tournaments}
            rowKey="tournament_id"
            pagination={pagination}
            onChange={handleTableChange}
            bordered
          />
        )}
      </div>

      {/* Mobile-friendly Modal */}
      <Modal
        title={isEditing ? "Edit Tournament" : "Create Tournament"}
        open={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
        }}
        onOk={() => form.submit()}
        okText={isEditing ? "Update" : "Create"}
        width="95%"  // <-- MOBILE FRIENDLY WIDTH
        style={{ top: 20 }}
        bodyStyle={{ paddingBottom: 0 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="tournament_id" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tournament Name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input placeholder="Tournament Name" />
          </Form.Item>

          <Form.Item
            name="sport"
            label="Sport"
            rules={[{ required: true, message: "Please select a sport" }]}
          >
            <Select placeholder="Select sport">
              <Option value="cricket">Cricket</Option>
              <Option value="tennis_cricket">Tennis Cricket</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select category">
              <Option value="domestic">Domestic</Option>
              <Option value="international">International</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Select status" }]}
          >
            <Select placeholder="Select status">
              <Option value="upcoming">Upcoming</Option>
              <Option value="live">Live</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="level"
            label="Level"
            rules={[{ required: true, message: "Select level" }]}
          >
            <Select placeholder="Select level">
              <Option value="international">International</Option>
              <Option value="domestic">Domestic</Option>
              <Option value="local">Local</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="date_range"
            label="Tournament Dates"
            rules={[{ required: true, message: "Select date range" }]}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Tournaments;
